/**
 * The address book's type-ahead (#159), for every kind (#221): the list, the keys and the asking,
 * shared by a party block's `PartyCombobox` and by the fields of one kind, `BookCombobox`.
 *
 * It follows the ARIA 1.2 combobox pattern with list autocomplete, and the DOM focus never leaves
 * the field:
 *
 *  - the field is the `combobox`: `aria-autocomplete="list"`, `aria-expanded` while the list shows,
 *    `aria-controls` naming the list, `aria-activedescendant` naming the option ↑ ↓ reached;
 *  - the list is a labelled `listbox` of `option`s, the reached one `aria-selected`;
 *  - ↓ and ↑ move through it and wrap, Enter chooses, Esc closes, and leaving the field closes it.
 *    Nothing is chosen except by Enter, a click or a tap.
 *
 * **A place is a textarea** (#221), and a textarea is a textbox that ARIA does not let be a
 * combobox: it keeps `aria-autocomplete`, `aria-controls` and `aria-activedescendant`, and loses the
 * role and `aria-expanded`. Its keys are the text's until the list opens: Enter writes a newline and
 * ↓ moves down a line. Once the list shows, ↑ ↓ move through it and Enter on a reached option
 * chooses it; Enter with none reached closes the list and still writes its newline, and the list stays
 * closed until the text says something new — the newline alone is not a search.
 *
 * The field is still the one react-hook-form registered: the same id, label, description,
 * `maxLength`, `disabled` and registration, with `onChange` and `onBlur` wrapped rather than
 * replaced. The form validates it as before, and a test that finds it by its label finds it.
 *
 * **What it asks for.** What the user types is the search: debounced, and nothing under two
 * characters. A value the form writes — a reopened draft, «Soy yo», a chosen suggestion — is not
 * typing and opens nothing. Nor does a search that matches nothing: the suggestions are a shortcut,
 * and a "no matches" box under every new name would only be in the way of typing it.
 *
 * **The list hangs under the field**, positioned inside it rather than fixed to the viewport as
 * `RowMenu`'s menu is: it scrolls with the form, and on a phone with the keyboard up there is no
 * viewport arithmetic to get wrong. That needs every ancestor up to the form to let it overflow,
 * which is why `.section-card` no longer clips its content.
 *
 * **Except in a table that scrolls sideways**: the grouped consignments' at a laptop's width. A
 * scroll box clips what hangs out of it, and letting it overflow instead lost the table's scroll and
 * widened the page under the field being typed in. There the list is fixed to the viewport, placed
 * from the field's rect as `RowMenu`'s is, and follows the field as anything scrolls, since it is
 * being typed into rather than chosen from. Where the table has nothing to scroll — a phone's
 * stacked rows, a wide screen — it hangs as everywhere else, marked so that the table lets it: a
 * list merely not yet fixed must not, or the table would lose its scroll in the frame before.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  SUGGESTION_LIMIT,
  SUGGESTION_MIN_LENGTH,
  type AddressBookItem,
  type AddressBookKind,
} from '../app/addressBook.ts'
import { useAddressBookSuggestions } from '../app/queries.ts'
import { columnOf, type BookField } from './bookField.ts'
import { fillColumn, replacesOtherText, useConfirmPartyOverwrite, writeParty } from './partyFill.tsx'
import { SaveFieldToBook } from './SaveFieldToBook.tsx'
import { useDebounced } from './useDebounced.ts'

/** How long typing has to pause before the book is asked — about a quarter of a second (spec D8). */
const DEBOUNCE_MS = 250

/** A fixed list's distance under the field (`--space-xs`, as the hanging list's), and from the screen's edge. */
const LIST_GAP = 4
const EDGE_GAP = 8

/** How an option reads: its first line, its second, and whether it says it comes from the documents. */
export interface OptionView {
  title: string | null
  detail: string | null
  fromDocuments: boolean
}

type FieldProps = ComponentPropsWithRef<'input'> | ComponentPropsWithRef<'textarea'>

/** The type-ahead itself, for one kind. `children` follow the list: a dialog a choice may open. */
export function BookTypeAhead({
  kind,
  input,
  multiline = false,
  view,
  onChoose,
  trailing,
  children,
}: {
  kind: AddressBookKind
  /** The field `BaseField` would otherwise have rendered, with its registration. */
  input: FieldProps
  multiline?: boolean
  view: (option: AddressBookItem) => OptionView
  onChoose: (option: AddressBookItem) => void
  /** Beside the field: «Guardar en la libreta» for a field of one kind. */
  trailing?: ReactNode
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const listboxId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  /** What was typed: the search. Set by typing, and by ↓ on a closed list, never by a fill. */
  const [term, setTerm] = useState('')
  /**
   * Whether a list may show: from typing, or ↓ on a closed list, until Esc, a choice, Enter with no
   * option reached, or leaving the field.
   */
  const [expanded, setExpanded] = useState(false)
  /** The option ↑ ↓ reached, or -1 while the user is in the text. */
  const [active, setActive] = useState(-1)
  /** What a textarea held, trimmed, when Enter closed its list: until it says more, nothing reopens it. */
  const closedOn = useRef<string | null>(null)
  /**
   * How an open list sits in a table: fixed to the viewport where the table scrolls sideways, or
   * hanging out of it where it has nothing to scroll. Null outside a table.
   */
  const [inTable, setInTable] = useState<{ top: number; left: number; minWidth: number } | 'hangs' | null>(null)

  const typed = term.trim()
  const search = useDebounced(typed, DEBOUNCE_MS)
  const suggestions = useAddressBookSuggestions(search, expanded, kind)
  // Both lengths: the debounced search is what was asked, and what is typed now may already be
  // shorter than that — a list of answers to "Ib" under a field that now reads "I" would be stale.
  const options =
    expanded && typed.length >= SUGGESTION_MIN_LENGTH && search.length >= SUGGESTION_MIN_LENGTH
      ? (suggestions.data?.data ?? []).slice(0, SUGGESTION_LIMIT)
      : []
  const open = options.length > 0
  const current = active < options.length ? active : -1
  const optionId = (index: number) => `${listboxId}-${index}`

  useEffect(() => {
    // The reached option kept in view in a list that scrolls. `?.`: jsdom has no `scrollIntoView`.
    if (current < 0) return
    listRef.current?.children[current]?.scrollIntoView?.({ block: 'nearest' })
  }, [current])

  useLayoutEffect(() => {
    const root = rootRef.current
    const box = root?.closest('.table-wrap')
    if (!open || !root || !box) return
    if (box.scrollWidth <= box.clientWidth) {
      setInTable('hangs')
      return () => setInTable(null)
    }
    const place = () => {
      // The viewport without its scrollbars, which is what `position: fixed` is measured against.
      const { clientWidth } = document.documentElement
      const anchor = root.getBoundingClientRect()
      const width = listRef.current?.offsetWidth ?? 0
      setInTable({
        top: anchor.bottom + LIST_GAP,
        // Under the field, unless that would push the list's right edge off the screen.
        left: Math.max(EDGE_GAP, Math.min(anchor.left, clientWidth - EDGE_GAP - width)),
        minWidth: anchor.width,
      })
    }
    function onScroll(event: Event) {
      // Capture catches every scroller's scroll; the list's own moves nothing it is placed from.
      if (event.target instanceof Node && listRef.current?.contains(event.target)) return
      place()
    }
    place()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
      setInTable(null)
    }
  }, [open])

  const close = () => {
    setExpanded(false)
    setActive(-1)
  }

  const choose = (option: AddressBookItem) => {
    close()
    onChoose(option)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    ;(input.onKeyDown as ((event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void) | undefined)?.(event)
    // While an input method is composing a character, Enter and the arrows are the composition's.
    if (event.nativeEvent.isComposing) return
    switch (event.key) {
      case 'ArrowDown':
        if (open) {
          event.preventDefault()
          setActive(current + 1 >= options.length ? 0 : current + 1)
        } else if (!multiline) {
          // A closed list opens on ↓ at its first option, searching what the field holds: after
          // Esc, or on a value the form wrote, which no keystroke has searched yet. In a textarea
          // ↓ is the caret's, a line down.
          event.preventDefault()
          setTerm(event.currentTarget.value)
          setExpanded(true)
          setActive(0)
        }
        break
      case 'ArrowUp':
        if (!open) break
        event.preventDefault()
        setActive(current <= 0 ? options.length - 1 : current - 1)
        break
      case 'Enter':
        if (!open) break
        if (current >= 0) {
          // Enter on a reached option is the list's: it never submits the form, reaches a button or
          // writes a newline.
          event.preventDefault()
          choose(options[current]!)
        } else {
          // None reached: the list closes. An input's Enter stops there; a textarea's newline is
          // still written, and its change is not a new search.
          if (multiline) closedOn.current = typed
          else event.preventDefault()
          close()
        }
        break
      case 'Escape':
        if (!expanded) break
        // On an open list this Escape is the list's: it stops here, so the drawer's listener on
        // `document` (bubble phase, `AppShell.tsx`) never sees it. The portal's `Dialog` listens in
        // the capture phase and would close first; no combobox sits in a dialog today. A search
        // still in flight is dropped too, so its answer does not open the list after Esc.
        if (open) {
          event.preventDefault()
          event.stopPropagation()
        }
        close()
        break
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
        // The caret moves in the text, and the reached option goes with it.
        setActive(-1)
        break
    }
  }

  const shared = {
    'aria-autocomplete': 'list' as const,
    'aria-controls': listboxId,
    'aria-activedescendant': current >= 0 ? optionId(current) : undefined,
    // The browser's own autofill list would open over this one.
    autoComplete: 'off',
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTerm(event.target.value)
      if (closedOn.current !== event.target.value.trim()) {
        closedOn.current = null
        setExpanded(true)
      }
      setActive(-1)
      ;(input.onChange as ((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void) | undefined)?.(event)
    },
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      close()
      ;(input.onBlur as ((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void) | undefined)?.(event)
    },
    onKeyDown,
  }
  const field = multiline ? (
    <textarea {...(input as ComponentPropsWithRef<'textarea'>)} {...shared} />
  ) : (
    <input {...(input as ComponentPropsWithRef<'input'>)} role="combobox" aria-expanded={open} {...shared} />
  )

  return (
    <div className="combobox" ref={rootRef}>
      {trailing ? (
        <div className="combobox__field">
          {field}
          {trailing}
        </div>
      ) : (
        field
      )}
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label={t('create.addressBook.suggestions')}
        className={
          inTable === null
            ? 'combobox__list'
            : `combobox__list combobox__list--${inTable === 'hangs' ? 'hangs' : 'fixed'}`
        }
        style={inTable === null || inTable === 'hangs' ? undefined : inTable}
        hidden={!open}
        // A press anywhere on the list (an option, the padding, the scrollbar) must not take the
        // focus out of the field: leaving the field closes the list under the pointer.
        onMouseDown={(event) => event.preventDefault()}
      >
        {options.map((option, index) => {
          const shown = view(option)
          return (
            <li
              key={option.entryId ?? `${option.kind}:${option.nif ?? option.plate ?? option.address}`}
              id={optionId(index)}
              role="option"
              aria-selected={index === current}
              className="combobox__option"
              // Chosen on the click, the press having kept the focus: a press dragged off an option
              // chooses nothing, and React gives `onClick`, not `onMouseDown`, the shim iOS Safari
              // needs before it sends a tap to an element that is not a control.
              onClick={() => choose(option)}
            >
              <span className="combobox__name">{shown.title}</span>
              {shown.detail ? <span className="combobox__detail">{shown.detail}</span> : null}
              {shown.fromDocuments ? (
                <span className="combobox__source">{t('addressBook.source.HISTORY')}</span>
              ) : null}
            </li>
          )
        })}
      </ul>
      {children}
    </div>
  )
}

/** How a place, a plate or a driver reads in the list. */
function viewOf(option: AddressBookItem): OptionView {
  const fromDocuments = option.source === 'HISTORY'
  switch (option.kind) {
    case 'PLACE':
      // A saved place's short name over its address; a place from the documents is its address.
      return option.name
        ? { title: option.name, detail: option.address, fromDocuments }
        : { title: option.address, detail: null, fromDocuments }
    case 'DRIVER':
      // Both values, so the office knows whose DNI/NIE it is choosing.
      return { title: option.name, detail: option.nif, fromDocuments }
    default:
      return { title: option.plate, detail: null, fromDocuments }
  }
}

/**
 * The type-ahead on a field of one kind (#221): a consignment's or a service's place, a vehicle's or
 * a trailer's plate, or the driver.
 *
 * **What choosing writes.** A place's address or a plate into the field, which is the search, so
 * nothing is asked. A driver's DNI/NIE, and where the form has a column for it, the name: a route
 * sheet's driver, whose name may already say something else, so the shared dialog asks first as a
 * party block's does. A goods DeCA has no column for the driver's name.
 *
 * **«Guardar en la libreta»** sits beside a place or a plate, and beside a route sheet's driver's
 * DNI/NIE, where both of the driver's values are typed. Not beside a goods DeCA's driver, which has
 * no name to save, nor beside a field the form has disabled.
 */
export function BookCombobox({
  book,
  input,
  multiline,
  label,
}: {
  book: BookField
  input: FieldProps
  multiline: boolean
  /** The field's label, which names its «Guardar en la libreta». */
  label: string
}) {
  const { t } = useTranslation()
  const form = useFormContext()
  const overwrite = useConfirmPartyOverwrite()

  const onChoose = (option: AddressBookItem) => {
    if (book.kind !== 'DRIVER') {
      fillColumn(form, book.column, (book.kind === 'PLACE' ? option.address : option.plate) ?? '')
      return
    }
    const values = { name: option.name ?? '', nif: option.nif ?? '' }
    if (!book.columns.name) {
      fillColumn(form, book.columns.nif, values.nif)
      return
    }
    const columns = { name: book.columns.name, nif: book.columns.nif }
    const fill = () => writeParty(form, columns, values)
    if (replacesOtherText(form, columns, values, columnOf(book) ?? columns.nif)) {
      overwrite.ask(values.name, fill)
    } else {
      fill()
    }
  }

  const saves =
    !input.disabled && (book.kind !== 'DRIVER' || (book.field === 'nif' && book.columns.name !== undefined))

  return (
    <BookTypeAhead
      kind={book.kind}
      input={input}
      multiline={multiline}
      view={viewOf}
      onChoose={onChoose}
      trailing={
        saves ? (
          <SaveFieldToBook book={book} accessibleName={t('create.saveToBook.fieldAction', { field: label })} />
        ) : undefined
      }
    >
      {overwrite.dialog}
    </BookTypeAhead>
  )
}
